import { useRef, useState } from 'react'

import { MatterBriefcase, RemoveUser, TrashDelete } from '@/assets/icons'
import { Modal, ModalContent, ModalFooterWithActions } from '@/components/modal'

export type JoinOrganizationModalProps = {
  /** Whether the modal is open. */
  visible: boolean
  /** Name of the organization being joined; also the value the user must type to confirm. */
  organizationName: string
  /** Called when the user dismisses the modal ("Not now", Escape, or outside click). */
  onClose: () => void
  /** Called when the user confirms joining (enabled only once the typed name matches). */
  onConfirm: () => void
  /** Drives the primary button's loading state while the parent performs the join. Default: false. */
  isJoining?: boolean
}

/** Consequences the user acknowledges before transferring their work into the organization. */
const kJoinWarnings = [
  {
    icon: <MatterBriefcase aria-hidden className="size-[18px] text-brand-neutral-600" />,
    text: 'All existing and future work in this account will belong to the organization.',
  },
  {
    icon: <RemoveUser aria-hidden className="size-[18px] text-brand-neutral-600" />,
    text: 'If the organization removes me later, I will lose access to my account.',
  },
  {
    icon: <TrashDelete aria-hidden className="size-[18px] text-brand-neutral-600" />,
    text: 'On removal, my AI chats and drafts are permanently deleted.',
  },
]

/**
 * Confirmation modal shown before a solo user joins an organization and transfers their existing
 * work. The join action is gated behind a type-to-confirm input: the user must type the exact
 * organization name before "Join organization" becomes enabled.
 */
export default function JoinOrganizationModal({ visible, organizationName, onClose, onConfirm, isJoining = false }: JoinOrganizationModalProps) {
  // The modal unmounts whenever it closes, so a reopened modal is a fresh component with an empty
  // field — no reset effect is needed to clear the confirmation input between opens.
  const [confirmName, setConfirmName] = useState('')
  const formRef = useRef<HTMLFormElement>(null)

  const isMatch = confirmName.trim() === organizationName.trim()
  const canConfirm = isMatch && !isJoining

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (canConfirm) onConfirm()
  }

  return (
    <Modal open={visible} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        title="Join organization and transfer work"
        width="md"
        height="max-lg"
        preventDismiss={isJoining}
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Not now', onClick: onClose, disabled: isJoining }}
            primaryAction={{
              label: 'Join organization',
              onClick: () => formRef.current?.requestSubmit(),
              disabled: !canConfirm,
              loading: isJoining,
            }}
          />
        }
      >
        <p className="font-sans text-base font-normal leading-6 tracking-[-0.32px] text-brand-neutral-950">
          Joining <strong className="font-semibold">{organizationName}</strong> moves your existing work into the organization. Verify before you continue:
        </p>

        <ul className="mt-6 flex flex-col gap-5">
          {kJoinWarnings.map((warning) => (
            <li key={warning.text} className="flex items-start gap-1.5">
              <span className="flex size-6 shrink-0 items-center justify-center">{warning.icon}</span>
              <span className="text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">{warning.text}</span>
            </li>
          ))}
        </ul>

        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="mt-6">
            <label htmlFor="join-org-confirm" className="block text-sm font-medium text-brand-neutral-600 mb-1">
              Type the organization name to confirm
            </label>
            <input
              id="join-org-confirm"
              type="text"
              value={confirmName}
              onChange={(event) => setConfirmName(event.target.value)}
              placeholder={organizationName}
              autoComplete="off"
              className="block w-full min-h-[56px] rounded border-0 bg-white px-3 py-2 text-brand-neutral-900 placeholder:text-brand-neutral-600 ring-1 ring-inset ring-brand-neutral-400 focus:ring-inset focus:outline-hidden focus:ring-brand-400 sm:text-sm"
            />
          </div>
        </form>
      </ModalContent>
    </Modal>
  )
}
